import {map, some} from "lodash";
import React, {FC, ReactElement, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useLocation} from "react-use";
import {InputUtils} from "../../../helpers";
import {IconChevronUp, IconLogout} from "../../assets";
import {Colors} from "../../colors";
import {
  Avatar,
  Box,
  CircleDot,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  Text,
  Tooltip,
} from "../../components";
import {Container} from "../Container";
import {ICategoryListItem, ICategoryNestListItem, ISublistItem, MenuPlatform, PermanentMenuHeaderProps} from "./types";
import {getMenuPlatformColors} from "./utils";
import {useMenuCollapsed} from "./MenuCollapsedContext";

const itemButtonStyle = (selected: boolean, platform: MenuPlatform, collapsed?: boolean) => {
  const {selectedBackground, hoverBackground} = getMenuPlatformColors(platform);
  const hover = {"&:hover": {backgroundColor: selected ? selectedBackground : hoverBackground}};
  const background = {backgroundColor: selected ? selectedBackground : null};
  return collapsed
    ? {
        borderRadius: "6px",
        padding: 0,
        minHeight: "40px",
        width: "40px",
        height: "40px",
        margin: "0 auto",
        justifyContent: "center",
        position: "relative",
        ...background,
        ...hover,
      }
    : {borderRadius: "6px", gap: "8px", padding: "0 12px", minHeight: "34px", ...background, ...hover};
};

const MaybeTooltip = ({when, title, children}: {when: boolean; title: string; children: ReactElement}) =>
  when ? (
    <Tooltip title={title} placement="right">
      {children}
    </Tooltip>
  ) : (
    children
  );

type MenuItemLabelProps = {title: string; selected: boolean; platform: MenuPlatform; subItem?: boolean};

const MenuItemLabel: FC<MenuItemLabelProps> = ({title, selected, platform, subItem}) => {
  const {accent} = getMenuPlatformColors(platform);
  return (
    <Text
      size={14}
      variant={selected ? "inter600" : subItem ? "inter400" : "inter500"}
      sx={{color: selected ? accent : Colors.grey._800}}
    >
      {InputUtils.capitalizeString(title)}
    </Text>
  );
};

export type CategoryListItemProps = {
  selected?: string;
  item: ICategoryListItem;
  platform: MenuPlatform;
};

export const CategoryListItem: FC<CategoryListItemProps> = ({item, selected, platform}) => {
  const navigate = useNavigate();
  const collapsed = useMenuCollapsed();
  const isSelected = selected === item.id;

  const onClickItem = () => {
    item.onClick?.();
    if (item.url) navigate(item.url);
  };

  return (
    <ListItem disablePadding sx={collapsed ? {display: "block"} : undefined}>
      <MaybeTooltip when={collapsed} title={InputUtils.capitalizeString(item.title)}>
        <ListItemButton sx={itemButtonStyle(isSelected, platform, collapsed)} onClick={onClickItem}>
          <ListItemIcon sx={{minWidth: 0, justifyContent: "center"}}>{item.icon}</ListItemIcon>
          {!collapsed && <MenuItemLabel title={item.title} selected={isSelected} platform={platform} />}
          {item.alert && (
            <CircleDot
              color={Colors.red._800}
              sx={
                collapsed
                  ? {width: "6px", height: "6px", position: "absolute", top: "6px", right: "6px"}
                  : {width: "6px", height: "6px"}
              }
            />
          )}
        </ListItemButton>
      </MaybeTooltip>
    </ListItem>
  );
};

export type CategoryNestListItemProps = {
  item: ICategoryNestListItem;
  selected?: string;
  platform: MenuPlatform;
};

export const CategoryNestListItem: FC<CategoryNestListItemProps> = ({item, selected, platform}) => {
  const navigate = useNavigate();
  const collapsed = useMenuCollapsed();
  const {pathname} = useLocation();
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  const pathInGroup = some(item.subListItems, ["url", pathname]);
  const [openOverride, setOpenOverride] = useState<boolean | null>(null);
  const open = openOverride ?? pathInGroup;

  const hasSelectedSubItem = some(item.subListItems, (s) => s.id === selected);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (collapsed) {
      setPopoverAnchor(event.currentTarget);
      return;
    }
    setOpenOverride(!open);
  };

  const handlePopoverClose = () => setPopoverAnchor(null);

  if (collapsed) {
    return (
      <>
        <ListItem disablePadding sx={{display: "block"}}>
          <Tooltip title={InputUtils.capitalizeString(item.title)} placement="right">
            <ListItemButton onClick={handleClick} sx={itemButtonStyle(hasSelectedSubItem, platform, true)}>
              <ListItemIcon sx={{minWidth: 0, justifyContent: "center"}}>{item.icon}</ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>
        <Menu
          anchorEl={popoverAnchor}
          open={Boolean(popoverAnchor)}
          onClose={handlePopoverClose}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          transformOrigin={{vertical: "top", horizontal: "left"}}
        >
          {map(item.subListItems, (subItem) => (
            <MenuItem
              key={subItem.id}
              selected={selected === subItem.id}
              onClick={() => {
                navigate(subItem.url);
                handlePopoverClose();
              }}
              sx={{minWidth: "180px"}}
            >
              <MenuItemLabel title={subItem.title} selected={selected === subItem.id} platform={platform} subItem />
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <>
      <ListItemButton onClick={handleClick} sx={{...itemButtonStyle(false, platform), justifyContent: "space-between"}}>
        <Container row>
          <ListItemIcon sx={{minWidth: 0}}>{item.icon}</ListItemIcon>
          <MenuItemLabel title={item.title} selected={false} platform={platform} />
        </Container>
        <IconChevronUp
          width={20}
          height={20}
          color={Colors.grey._500}
          style={{...(!open && {transform: "rotate(180deg)"})}}
        />
      </ListItemButton>
      <Collapse in={open} unmountOnExit>
        <List component="div" disablePadding>
          {map(item.subListItems, (subItem: ISublistItem) => (
            <ListItemButton
              key={subItem.title}
              onClick={() => navigate(subItem.url)}
              sx={{
                ...itemButtonStyle(selected === subItem.id, platform),
                padding: 0,
                paddingLeft: "44px",
                paddingRight: "12px",
                minHeight: "34px",
                height: "34px",
              }}
            >
              <MenuItemLabel title={subItem.title} selected={selected === subItem.id} platform={platform} subItem />
            </ListItemButton>
          ))}
        </List>
      </Collapse>
    </>
  );
};

const getInitials = (fullName?: string): string | null => {
  const parts = fullName?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (parts.length === 0) return null;
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts.at(-1)!.charAt(0) : "";
  return (first + last).toUpperCase();
};

export const PermanentMenuHeader = ({
  fullName,
  email,
  isLoading,
  onSignOut,
  onHomeClick,
  platform,
}: PermanentMenuHeaderProps) => {
  const collapsed = useMenuCollapsed();
  const {selectedBackground, accent} = getMenuPlatformColors(platform);
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setShowSkeleton(false);
      return;
    }
    const timer = setTimeout(() => setShowSkeleton(true), 150);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const initials = getInitials(fullName);
  const avatar = initials ? (
    <Avatar
      sx={{
        width: 32,
        height: 32,
        bgcolor: selectedBackground,
        color: accent,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {initials}
    </Avatar>
  ) : null;

  const loadingAvatar = showSkeleton ? (
    <Skeleton variant="circular" width={32} height={32} />
  ) : (
    <Box sx={{width: 32, height: 32}} />
  );
  const loadingName = showSkeleton ? <Skeleton variant="text" width={110} height={20} /> : <Box sx={{height: 20}} />;

  if (collapsed) {
    return (
      <Container row fullWidth sx={{justifyContent: "center"}}>
        {isLoading ? (
          loadingAvatar
        ) : (
          <Tooltip title={fullName ?? email ?? ""} placement="right">
            <Box>{avatar}</Box>
          </Tooltip>
        )}
      </Container>
    );
  }

  return (
    <Container
      row
      fullWidth
      sx={{
        padding: "8px 12px",
        border: `1px solid ${Colors.tertiary._300}`,
        borderRadius: "8px",
      }}
    >
      <Box sx={{flexShrink: 0, cursor: "pointer"}} onClick={onHomeClick}>
        {isLoading ? loadingAvatar : avatar}
      </Box>
      <Box onClick={onHomeClick} sx={{flex: 1, minWidth: 0, cursor: "pointer", overflow: "hidden"}}>
        {isLoading
          ? loadingName
          : fullName && (
              <Text
                size={14}
                variant="inter600"
                sx={{
                  color: Colors.grey._800,
                  lineHeight: "20px",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {fullName}
              </Text>
            )}
        <Text
          size={12}
          sx={{
            color: Colors.tertiary._600,
            lineHeight: "18px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {email}
        </Text>
      </Box>
      <Tooltip title="Sign out" placement="right">
        <IconButton onClick={onSignOut} sx={{padding: "4px", flexShrink: 0}} disableRipple>
          <IconLogout width={18} height={18} color={Colors.tertiary._600} />
        </IconButton>
      </Tooltip>
    </Container>
  );
};
